import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, UserPlus, Trash2, Shield, UserCheck, UserX, KeyRound, History, Lock, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const EMPTY_BRACKET = { from: 0, to: 0, primary: 0, add_pct: 0 };
const EMPTY_SETTINGS = { square_meters_default: 350, rate_per_sqm_default: 16000, discount: 1.0 };

function fmt(n) {
  if (n === null || n === undefined || n === '') return '';
  return Number(n).toLocaleString('en-ZA');
}

// Confirm/handoff modal — appears when a user has active projects and is being
// deactivated or deleted. Offers one-click reassignment to another active user.
function OwnershipHandoffDialog({ open, targetUser, projects, candidates, actionLabel, onConfirm, onCancel, working }) {
  const [reassignTo, setReassignTo] = useState('');
  useEffect(() => { setReassignTo(''); }, [open, targetUser?.user_id]);
  if (!open || !targetUser) return null;
  const hasProjects = projects && projects.length > 0;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="ownership-handoff-dialog">
      <div className="bg-white rounded-sm border border-black/10 max-w-lg w-full p-6">
        <div className="flex items-start gap-3 mb-4">
          <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-brand-dark">
              {hasProjects ? 'Reassign active projects?' : `Confirm ${actionLabel}`}
            </h3>
            <p className="text-[13px] text-brand-dark/60 mt-1">
              {hasProjects
                ? `${targetUser.name || targetUser.email} owns ${projects.length} active project${projects.length > 1 ? 's' : ''}. Reassign them before proceeding.`
                : `Proceed with ${actionLabel} for ${targetUser.email}?`}
            </p>
          </div>
        </div>

        {hasProjects && (
          <div className="border border-black/10 rounded-sm p-3 mb-4 bg-brand-light/40 max-h-40 overflow-auto" data-testid="handoff-project-list">
            {projects.map(p => (
              <div key={p.project_id} className="text-[13px] text-brand-dark/70 py-1 border-b border-black/5 last:border-0">
                <span className="font-medium">{p.name}</span>
                {p.client_name && <span className="text-brand-dark/40 ml-2">· {p.client_name}</span>}
              </div>
            ))}
          </div>
        )}

        {hasProjects && (
          <div className="mb-4">
            <Label className="text-xs text-brand-dark/60">Reassign to</Label>
            <Select value={reassignTo} onValueChange={setReassignTo}>
              <SelectTrigger className="h-9 rounded-sm text-sm" data-testid="handoff-select-target">
                <SelectValue placeholder="Select another active user..." />
              </SelectTrigger>
              <SelectContent>
                {candidates.map(c => (
                  <SelectItem key={c.user_id} value={c.user_id}>
                    {c.name || c.email} <span className="text-brand-dark/40 ml-2">({c.role})</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button size="sm" variant="ghost" onClick={onCancel} disabled={working} data-testid="handoff-cancel">
            Cancel
          </Button>
          <Button size="sm" onClick={() => onConfirm(reassignTo)} disabled={working || (hasProjects && !reassignTo)}
            data-testid="handoff-confirm"
            className="bg-brand-dark text-white hover:bg-gray-800 rounded-sm font-body text-xs uppercase tracking-wider">
            {working ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (hasProjects ? `Reassign and ${actionLabel}` : actionLabel)}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ================= USERS =================
function UsersSection({ currentUser, onAuditChanged }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', name: '', password: '', role: 'architect' });
  const [resetFor, setResetFor] = useState(null);
  const [resetPwd, setResetPwd] = useState('');

  // Handoff state: for deactivate / delete, we first fetch owned projects and show a modal if any.
  const [handoff, setHandoff] = useState(null); // { user, projects, action: 'deactivate'|'delete' }
  const [handoffWorking, setHandoffWorking] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/users');
      setUsers(data);
    } catch { toast.error('Failed to load users'); }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const createUser = async () => {
    if (!newUser.email || !newUser.name || !newUser.password) {
      toast.error('Email, name and password are required');
      return;
    }
    if (newUser.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setLoading(true);
    try {
      await api.post('/admin/users', newUser);
      toast.success(`${newUser.name} added as ${newUser.role}`);
      setNewUser({ email: '', name: '', password: '', role: 'architect' });
      setShowCreate(false);
      await fetchUsers();
      onAuditChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create user');
    } finally { setLoading(false); }
  };

  const updateUser = async (user_id, patch, successMsg) => {
    try {
      await api.patch(`/admin/users/${user_id}`, patch);
      toast.success(successMsg || 'User updated');
      await fetchUsers();
      onAuditChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  const deleteUser = async (u) => {
    await beginHandoff(u, 'delete');
  };

  // --- Ownership handoff helpers ---
  const beginHandoff = async (u, action) => {
    try {
      const { data } = await api.get(`/admin/users/${u.user_id}/active-projects`);
      setHandoff({ user: u, projects: data.projects || [], action });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to check owned projects');
    }
  };

  const finishHandoff = async (reassignToId) => {
    if (!handoff) return;
    setHandoffWorking(true);
    try {
      // If there are owned projects, reassign first
      if (handoff.projects.length > 0) {
        if (!reassignToId) { toast.error('Select a user to reassign to'); setHandoffWorking(false); return; }
        const { data } = await api.post(`/admin/users/${handoff.user.user_id}/reassign`, { to_user_id: reassignToId });
        toast.success(`${data.count} project${data.count === 1 ? '' : 's'} reassigned`);
      }
      // Now perform the original action
      if (handoff.action === 'delete') {
        await api.delete(`/admin/users/${handoff.user.user_id}`);
        toast.success('User deleted');
      } else if (handoff.action === 'deactivate') {
        await api.patch(`/admin/users/${handoff.user.user_id}`, { active: false });
        toast.success('Deactivated — sessions revoked');
      }
      setHandoff(null);
      await fetchUsers();
      onAuditChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally {
      setHandoffWorking(false);
    }
  };

  const submitPasswordReset = async () => {
    if (!resetPwd || resetPwd.length < 8) { toast.error('Min 8 characters'); return; }
    await updateUser(resetFor.user_id, { password: resetPwd }, 'Password reset — active sessions revoked');
    setResetFor(null); setResetPwd('');
  };

  return (
    <div className="bg-white rounded-sm border border-black/10 p-6" data-testid="admin-users-section">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-xs font-heading uppercase tracking-wider text-brand-dark/50 flex items-center gap-2">
            <Shield className="w-3.5 h-3.5" /> Studio Access · Users
          </h3>
          <p className="text-[11px] text-brand-dark/40 font-body mt-1">Admins have full access. Architects have operational access only.</p>
        </div>
        <Button size="sm" onClick={() => setShowCreate(s => !s)} data-testid="admin-users-add-toggle"
          className="bg-brand-dark text-white hover:bg-gray-800 rounded-sm font-body text-xs uppercase tracking-wider">
          <UserPlus className="w-3.5 h-3.5 mr-1.5" /> {showCreate ? 'Cancel' : 'Add user'}
        </Button>
      </div>

      {showCreate && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-5 p-4 bg-brand-light/50 border border-black/5 rounded-sm" data-testid="admin-users-create-form">
          <div className="md:col-span-1">
            <Label className="text-xs">Name</Label>
            <Input value={newUser.name} onChange={e => setNewUser(u => ({ ...u, name: e.target.value }))} className="h-9 rounded-sm text-sm" data-testid="admin-users-new-name" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs">Email</Label>
            <Input type="email" value={newUser.email} onChange={e => setNewUser(u => ({ ...u, email: e.target.value }))} className="h-9 rounded-sm text-sm" data-testid="admin-users-new-email" />
          </div>
          <div className="md:col-span-1">
            <Label className="text-xs">Password</Label>
            <Input type="password" value={newUser.password} onChange={e => setNewUser(u => ({ ...u, password: e.target.value }))} className="h-9 rounded-sm text-sm" placeholder="min 8 chars" data-testid="admin-users-new-pwd" />
          </div>
          <div className="md:col-span-1">
            <Label className="text-xs">Role</Label>
            <Select value={newUser.role} onValueChange={v => setNewUser(u => ({ ...u, role: v }))}>
              <SelectTrigger className="h-9 rounded-sm text-sm" data-testid="admin-users-new-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="architect">Architect</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-5 flex justify-end">
            <Button size="sm" onClick={createUser} disabled={loading} data-testid="admin-users-create-submit"
              className="bg-brand-accent text-brand-dark hover:bg-brand-accent/90 rounded-sm font-body text-xs uppercase tracking-wider">
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Create user'}
            </Button>
          </div>
        </div>
      )}

      <div className="border border-black/5 rounded-sm overflow-hidden" data-testid="admin-users-table">
        <table className="w-full text-sm font-body">
          <thead className="bg-brand-light/50 text-[10px] uppercase tracking-wider text-brand-dark/40">
            <tr>
              <th className="text-left px-3 py-2">Name</th>
              <th className="text-left px-3 py-2">Email</th>
              <th className="text-left px-3 py-2">Role</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Source</th>
              <th className="text-right px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => {
              const isSelf = u.email === currentUser?.email;
              return (
                <tr key={u.user_id} className="border-t border-black/5 hover:bg-brand-light/30" data-testid={`admin-user-row-${u.user_id}`}>
                  <td className="px-3 py-2 text-brand-dark/80">{u.name || '—'}{isSelf && <span className="ml-1 text-[10px] text-brand-dark/40">(you)</span>}</td>
                  <td className="px-3 py-2 text-brand-dark/60">{u.email}</td>
                  <td className="px-3 py-2">
                    <Select value={u.role} onValueChange={v => !isSelf && updateUser(u.user_id, { role: v }, `${u.email} → ${v}`)} disabled={isSelf}>
                      <SelectTrigger className="h-7 w-28 rounded-sm text-xs" data-testid={`admin-user-role-${u.user_id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="architect">Architect</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-3 py-2">
                    {u.active ? (
                      <Badge variant="outline" className="text-[10px] border-green-300 text-green-700 bg-green-50">Active</Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px] border-red-300 text-red-600 bg-red-50">Inactive</Badge>
                    )}
                  </td>
                  <td className="px-3 py-2 text-[11px] text-brand-dark/40 uppercase tracking-wider">{u.auth_provider || 'password'}</td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex items-center gap-1 justify-end">
                      <Button size="sm" variant="ghost" disabled={isSelf}
                        onClick={() => u.active ? beginHandoff(u, 'deactivate') : updateUser(u.user_id, { active: true }, 'Reactivated')}
                        className="h-7 px-2 text-xs" data-testid={`admin-user-toggle-${u.user_id}`}
                        title={u.active ? 'Deactivate' : 'Activate'}>
                        {u.active ? <UserX className="w-3.5 h-3.5" /> : <UserCheck className="w-3.5 h-3.5" />}
                      </Button>
                      {u.auth_provider !== 'google' && (
                        <Button size="sm" variant="ghost" onClick={() => { setResetFor(u); setResetPwd(''); }}
                          className="h-7 px-2 text-xs" data-testid={`admin-user-resetpwd-${u.user_id}`}
                          title="Reset password">
                          <KeyRound className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" disabled={isSelf} onClick={() => deleteUser(u)}
                        className="h-7 px-2 text-xs text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`admin-user-delete-${u.user_id}`}
                        title="Delete">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {resetFor && (
        <div className="mt-4 p-4 bg-amber-50/60 border border-amber-200 rounded-sm" data-testid="admin-users-reset-pane">
          <div className="text-xs font-heading uppercase tracking-wider text-amber-700 mb-2">
            Reset password for {resetFor.email}
          </div>
          <div className="flex items-end gap-2">
            <div className="flex-1 max-w-sm">
              <Label className="text-xs">New password (min 8 chars)</Label>
              <Input type="password" value={resetPwd} onChange={e => setResetPwd(e.target.value)} className="h-9 rounded-sm text-sm" data-testid="admin-users-reset-input" />
            </div>
            <Button size="sm" onClick={submitPasswordReset} data-testid="admin-users-reset-submit"
              className="bg-brand-dark text-white hover:bg-gray-800 rounded-sm font-body text-xs uppercase tracking-wider">Reset</Button>
            <Button size="sm" variant="ghost" onClick={() => setResetFor(null)} className="text-xs">Cancel</Button>
          </div>
          <p className="text-[11px] text-amber-700/70 mt-2">Active sessions will be revoked immediately.</p>
        </div>
      )}

      <OwnershipHandoffDialog
        open={!!handoff}
        targetUser={handoff?.user}
        projects={handoff?.projects || []}
        candidates={users.filter(u => u.active && u.user_id !== handoff?.user?.user_id)}
        actionLabel={handoff?.action === 'delete' ? 'delete user' : 'deactivate'}
        working={handoffWorking}
        onConfirm={finishHandoff}
        onCancel={() => setHandoff(null)}
      />
    </div>
  );
}

// ================= FEE SCALE =================
export function FeeScaleSection({ readOnly, onSaved }) {
  const [fee, setFee] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/config');
      setFee({
        fee_settings: data.fee_settings || EMPTY_SETTINGS,
        fee_brackets: (data.fee_brackets || []).map(b => ({ ...b })),
      });
      setDirty(false);
    } catch { toast.error('Failed to load fee scale'); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateSetting = (k, v) => {
    setFee(f => ({ ...f, fee_settings: { ...f.fee_settings, [k]: Number(v) } }));
    setDirty(true);
  };
  const updateBracket = (i, k, v) => {
    setFee(f => {
      const copy = [...f.fee_brackets];
      copy[i] = { ...copy[i], [k]: Number(v) };
      return { ...f, fee_brackets: copy };
    });
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data: current } = await api.get('/config');
      const payload = { ...current, fee_settings: fee.fee_settings, fee_brackets: fee.fee_brackets };
      await api.put('/config', payload);
      toast.success('Fee scale saved — applied to calculators, reports and Studio');
      setDirty(false);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  if (!fee) {
    return <div className="bg-white rounded-sm border border-black/10 p-6"><Loader2 className="w-5 h-5 animate-spin text-brand-dark/30" /></div>;
  }

  return (
    <div className="bg-white rounded-sm border border-black/10 p-6" data-testid="fee-scale-section">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-xs font-heading uppercase tracking-wider text-brand-dark/50 flex items-center gap-2">
            <Shield className="w-3.5 h-3.5" /> Practice Configuration · SACAP Sliding Scale
          </h3>
          <p className="text-[11px] text-brand-dark/40 font-body mt-1">
            {readOnly ? 'How the practice prices work. Admin-managed.' : 'Edits here flow immediately into the calculators, generated reports and Studio projections.'}
          </p>
        </div>
        {readOnly && (
          <Badge variant="outline" className="text-[10px] border-black/10 text-brand-dark/40 font-body">
            <Lock className="w-3 h-3 mr-1" /> Read-only
          </Badge>
        )}
      </div>

      {/* Top settings row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
        <div>
          <Label className="text-xs text-brand-dark/50">Square Metres (default)</Label>
          <Input type="number" value={fee.fee_settings.square_meters_default ?? ''} onChange={e => updateSetting('square_meters_default', e.target.value)}
            disabled={readOnly} className="h-9 rounded-sm text-sm" data-testid="fee-setting-sqm" />
        </div>
        <div>
          <Label className="text-xs text-brand-dark/50">Estimated Rate / m² (ZAR)</Label>
          <Input type="number" value={fee.fee_settings.rate_per_sqm_default ?? ''} onChange={e => updateSetting('rate_per_sqm_default', e.target.value)}
            disabled={readOnly} className="h-9 rounded-sm text-sm" data-testid="fee-setting-rate" />
        </div>
        <div>
          <Label className="text-xs text-brand-dark/50">Discount Factor (1.0 = full, 0.5 = 50%)</Label>
          <Input type="number" step="0.05" min="0.1" max="1" value={fee.fee_settings.discount ?? 1} onChange={e => updateSetting('discount', e.target.value)}
            disabled={readOnly} className="h-9 rounded-sm text-sm" data-testid="fee-setting-discount" />
        </div>
      </div>

      {/* Bracket table */}
      <div className="border border-black/5 rounded-sm overflow-hidden" data-testid="fee-bracket-table">
        <table className="w-full text-sm font-body">
          <thead className="bg-brand-light/50 text-[10px] uppercase tracking-wider text-brand-dark/40">
            <tr>
              <th className="text-left px-3 py-2 w-10">#</th>
              <th className="text-right px-3 py-2">From (ZAR)</th>
              <th className="text-right px-3 py-2">To (ZAR)</th>
              <th className="text-right px-3 py-2">Primary Fee (ZAR)</th>
              <th className="text-right px-3 py-2">Add %</th>
            </tr>
          </thead>
          <tbody>
            {fee.fee_brackets.map((b, i) => (
              <tr key={i} className="border-t border-black/5" data-testid={`fee-bracket-row-${i}`}>
                <td className="px-3 py-2 text-brand-dark/40 text-xs">{i + 1}</td>
                <td className="px-2 py-1 text-right">
                  <Input type="number" value={b.from ?? ''} onChange={e => updateBracket(i, 'from', e.target.value)}
                    disabled={readOnly} className="h-8 rounded-sm text-xs text-right" />
                </td>
                <td className="px-2 py-1 text-right">
                  <Input type="number" value={b.to ?? ''} onChange={e => updateBracket(i, 'to', e.target.value)}
                    disabled={readOnly} className="h-8 rounded-sm text-xs text-right" />
                </td>
                <td className="px-2 py-1 text-right">
                  <Input type="number" value={b.primary ?? ''} onChange={e => updateBracket(i, 'primary', e.target.value)}
                    disabled={readOnly} className="h-8 rounded-sm text-xs text-right" />
                </td>
                <td className="px-2 py-1 text-right">
                  <Input type="number" step="0.01" value={b.add_pct ?? ''} onChange={e => updateBracket(i, 'add_pct', e.target.value)}
                    disabled={readOnly} className="h-8 rounded-sm text-xs text-right w-20 ml-auto" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!readOnly && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-[11px] text-brand-dark/40 font-body">
            {dirty ? 'Unsaved changes' : `Scale confirmed · ${fee.fee_brackets.length} brackets`}
          </p>
          <Button onClick={save} disabled={saving || !dirty} data-testid="fee-scale-save"
            className="bg-brand-dark text-white hover:bg-gray-800 rounded-sm font-body text-xs uppercase tracking-wider">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Save fee scale'}
          </Button>
        </div>
      )}
    </div>
  );
}

// ================= AUDIT =================
function AuditSection({ refreshTick }) {
  const [entries, setEntries] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/audit?limit=20');
      setEntries(data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { load(); }, [load, refreshTick]);

  if (!entries.length) {
    return null;
  }

  const describe = (e) => {
    const d = e.data || {};
    switch (e.action) {
      case 'user_created': return `Created ${d.target_email} as ${d.role}`;
      case 'user_updated': {
        const ch = d.changes || {};
        const parts = [];
        if (ch.role) parts.push(`role ${ch.role.from}→${ch.role.to}`);
        if ('active' in ch) parts.push(ch.active ? 'reactivated' : 'deactivated');
        if (ch.name) parts.push(`renamed to ${ch.name}`);
        if (ch.password_reset) parts.push('password reset');
        return `Updated ${d.target_email} — ${parts.join(', ') || 'no-op'}`;
      }
      case 'user_deleted': return `Deleted ${d.target_email}`;
      case 'config_updated': return `Config changed: ${(d.changed_keys || []).join(', ') || '—'}`;
      default: return e.action;
    }
  };

  return (
    <div className="bg-white rounded-sm border border-black/10 p-6" data-testid="admin-audit-section">
      <h3 className="text-xs font-heading uppercase tracking-wider text-brand-dark/50 flex items-center gap-2 mb-3">
        <History className="w-3.5 h-3.5" /> Recent Admin Activity
      </h3>
      <div className="space-y-2">
        {entries.map(e => (
          <div key={e.audit_id} className="flex items-start justify-between gap-3 py-1.5 border-b border-black/5 last:border-0 text-sm font-body" data-testid={`audit-entry-${e.audit_id}`}>
            <div className="flex-1">
              <p className="text-brand-dark/80 text-[13px]">{describe(e)}</p>
              <p className="text-[11px] text-brand-dark/40">by {e.actor_email}</p>
            </div>
            <span className="text-[11px] text-brand-dark/40 whitespace-nowrap">
              {new Date(e.timestamp).toLocaleString('en-ZA', { dateStyle: 'short', timeStyle: 'short' })}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ================= CONTAINER =================
export default function StudioAdminPanel({ user }) {
  const isAdmin = user?.role === 'admin';
  const [auditTick, setAuditTick] = useState(0);
  const bumpAudit = () => setAuditTick(t => t + 1);

  return (
    <div className="space-y-4" data-testid="studio-admin-panel">
      {isAdmin && <UsersSection currentUser={user} onAuditChanged={bumpAudit} />}
      <FeeScaleSection readOnly={!isAdmin} onSaved={bumpAudit} />
      {isAdmin && <AuditSection refreshTick={auditTick} />}
    </div>
  );
}
